import React, { useState } from "react";
import { useAppTranslation } from "../../../i18n/useAppTranslation";
import { extractApiErrorData } from "../../../utilities/api";
import { uiNotification } from "../../../services/UINotificationService";
import { ModalError } from "../../../boxes/ModalError/ModalError";
import { useResendPortalBulkInviteMutation } from "../../../api/patient/useResendPortalBulkInviteMutation";
import { BulkInviteSummaryModal } from "../BulkInviteSummaryModal";

const INVITE_LIMIT = 20;
const SUMMARY_MODAL_THRESHOLD = 10;

export function withBulkInvite(Component) {
    return (props) => {
      const { tClients } = useAppTranslation.Clients();
  
      const [selected, setSelected] = useState([]);
      const [errors, setErrors] = useState([]);
      const [showSummaryModal, setShowSummaryModal] = useState(false);
      const [summaryData, setSummaryData] = useState({
        eligible: [],
        doNotEmailCount: 0,
        firedCount: 0,
      });
      const [pendingCallback, setPendingCallback] = useState(null);
  
      const { mutateAsync, isLoading } = useResendPortalBulkInviteMutation({
        onError: (e) => {
          const errors = extractApiErrorData(e)?.errors || [];
  
          if (errors.length) {
            setErrors(errors);
           
          } else {
            uiNotification.error(tClients("bulkInvite.error"));
          }
          setShowSummaryModal(false);
        },
        onSuccess: (data, variables) => {
          const errors = data?.data?.data?.errors || [];
  
          if (errors.length) {
            setErrors(errors);
          } else {
            setSelected([]);
            setErrors([]);
            setShowSummaryModal(false);

            const { sentCount, skippedCount } = variables;
            if (skippedCount === 0) {
              uiNotification.success(
                tClients("bulkInvite.success.allEligible", { count: sentCount })
              );
            } else if (sentCount === 0) {
              uiNotification.warning(
                tClients("bulkInvite.success.allBlocked")
              );
            } else {
              uiNotification.success(
                tClients("bulkInvite.success.mixed", {
                  sent: sentCount,
                  skipped: skippedCount,
                })
              );
            }
          }
        },
      });
  
      const getIsSelected = (id) => selected.includes(id);
  
      const toggleSelected = (id) => {
        if (selected.includes(id)) {
          setSelected(selected.filter((i) => i !== id));
        } else {
          setSelected([...selected, id]);
        }
      };
      
      const analyzePatients = (patientIds, allPatients) => {
        const eligible = [];
        let doNotEmailCount = 0;
        let firedCount = 0;

        patientIds.forEach((id) => {
          const patient = allPatients.find((p) => p.id === id);
          if (!patient) {
            return;
          }

          const isBlocked = patient.do_not_email || patient.is_fired;
          
          if (isBlocked) {
            if (patient.do_not_email) doNotEmailCount++;
            if (patient.is_fired) firedCount++;
          } else {
            eligible.push(id);
          }
        });

        return {
          eligible,
          doNotEmailCount,
          firedCount,
          skippedCount: patientIds.length - eligible.length,
        };
      };

      const sendInvites = (patientIds, sentCount, skippedCount) => {
        return mutateAsync({ 
          patientIds: patientIds.map(Number),
          sentCount,
          skippedCount,
        });
      };
      
      const onSendInvites = (allPatients, nextOnSuccess) => {
        if(selected.length > INVITE_LIMIT) {
          return Promise.reject(
            uiNotification.error(tClients("bulkInvite.tooMany"))
          );
        }

        const analysis = analyzePatients(selected, allPatients);
        const { eligible, doNotEmailCount, firedCount, skippedCount } = analysis;
        
        if (eligible.length === 0) {
          uiNotification.warning(tClients("bulkInvite.success.allBlocked"));
          return Promise.resolve();
        }

        if (selected.length > SUMMARY_MODAL_THRESHOLD) {
          setSummaryData({
            eligible,
            doNotEmailCount,
            firedCount,
          });
          setPendingCallback(() => nextOnSuccess);
          setShowSummaryModal(true);
          return Promise.resolve();
        }

        return sendInvites(eligible, eligible.length, skippedCount).then(nextOnSuccess);
      };

      const handleConfirmSummary = () => {
        const { eligible } = summaryData;
        const skippedCount = selected.length - eligible.length;
        sendInvites(eligible, eligible.length, skippedCount).then(() => {
          if (pendingCallback) {
            pendingCallback();
            setPendingCallback(null);
          }
        });
      };

      const handleCloseSummary = () => {
        setShowSummaryModal(false);
        setPendingCallback(null);
      };

      return (
        <>
          <Component
            {...props}
            bulkInvite={{
              getIsSelected,
              toggleSelected,
              isLoading,
              onBulkSelect: (selectedUsers) => setSelected(selectedUsers),
              count: selected.length,
              request: (nextOnSuccess, allPatients) => {
                onSendInvites(allPatients, nextOnSuccess);
              },
              reset: () => {
                setSelected([]);
              },
            }}
          />
          {errors.length > 0 && (
            <ModalError
              isOpen
              onClose={() => setErrors([])}
              onOk={() => setErrors([])}
            >
              <div className="d-flex flex-column gap-4">
                <h4 className="m-b-10">{tClients("bulkInvite.cannotInvite")}:</h4>
                {errors.map((error) => (
                  <div key={error.patient_id}>
                    <b>{error.patient_name} </b>
                    {tClients(`bulkInvite.apiError.${error.error}`, error.error)}
                  </div>
                ))}
              </div>
            </ModalError>
          )}
          <BulkInviteSummaryModal
            isOpen={showSummaryModal}
            onClose={handleCloseSummary}
            onConfirm={handleConfirmSummary}
            eligibleCount={summaryData.eligible?.length || 0}
            totalCount={selected.length}
            doNotEmailCount={summaryData.doNotEmailCount}
            firedCount={summaryData.firedCount}
            isLoading={isLoading}
          />
        </>
      );
    };
}
